/**
 * Serge Cart Drawer
 * Responsive cart drawer - opens drawer on desktop (990px+), links to cart page on mobile
 */
(function() {
  'use strict';

  const MOBILE_BREAKPOINT = 990;

  const SergeCartDrawer = {
    drawer: null,
    isOpen: false,
    focusedElementBeforeOpen: null,

    selectors: {
      drawer: '[data-serge-cart-drawer]',
      close: '[data-cart-drawer-close]',
      trigger: '[data-serge-cart-trigger]',
      content: '[data-cart-drawer-content]',
      items: '[data-cart-drawer-items]',
      empty: '[data-cart-drawer-empty]',
      footer: '[data-cart-drawer-footer]',
      count: '[data-cart-drawer-count] span',
      subtotal: '[data-cart-subtotal]',
      qtyChange: '[data-qty-change]',
      qtyInput: '[data-qty-input]',
      removeItem: '[data-remove-item]',
      cartItem: '[data-cart-item]',
      headerCartCount: '[data-cart-count]'
    },

    init: function() {
      this.drawer = document.querySelector(this.selectors.drawer);
      if (!this.drawer) return;

      this.bindEvents();
      this.setupCartTriggers();
    },

    bindEvents: function() {
      // Close buttons and overlay
      this.drawer.querySelectorAll(this.selectors.close).forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Quantity buttons (event delegation)
      this.drawer.addEventListener('click', (e) => {
        const qtyBtn = e.target.closest(this.selectors.qtyChange);
        if (qtyBtn) {
          e.preventDefault();
          const delta = parseInt(qtyBtn.dataset.qtyChange, 10);
          const item = qtyBtn.closest(this.selectors.cartItem);
          const input = item.querySelector(this.selectors.qtyInput);
          const currentQty = parseInt(input.value, 10) || 0;
          const newQty = Math.max(0, currentQty + delta);
          this.updateQuantity(item, newQty);
        }
      });

      // Quantity input change
      this.drawer.addEventListener('change', (e) => {
        if (e.target.matches(this.selectors.qtyInput)) {
          const item = e.target.closest(this.selectors.cartItem);
          const newQty = Math.max(0, parseInt(e.target.value, 10) || 0);
          this.updateQuantity(item, newQty);
        }
      });

      // Remove item
      this.drawer.addEventListener('click', (e) => {
        const removeBtn = e.target.closest(this.selectors.removeItem);
        if (removeBtn) {
          e.preventDefault();
          const item = removeBtn.closest(this.selectors.cartItem);
          this.updateQuantity(item, 0);
        }
      });

      // Handle window resize - close drawer if resized to mobile
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (this.isMobile() && this.isOpen) {
            this.close();
          }
        }, 100);
      });
    },

    setupCartTriggers: function() {
      // Find all cart triggers (header cart icon)
      const triggers = document.querySelectorAll(this.selectors.trigger);

      triggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          // Check if mobile
          if (this.isMobile()) {
            // Allow default link behavior (go to /cart)
            return;
          }

          // Desktop: prevent default and open drawer
          e.preventDefault();
          this.open(trigger);
        });
      });
    },

    isMobile: function() {
      return window.innerWidth < MOBILE_BREAKPOINT;
    },

    open: function(triggeredBy) {
      if (this.isMobile()) return;

      // Clear any stuck loading states
      this.drawer.querySelectorAll('.is-loading').forEach(el => {
        el.classList.remove('is-loading');
      });

      this.focusedElementBeforeOpen = document.activeElement;
      this.drawer.classList.add('is-active');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.isOpen = true;
      document.body.style.overflow = 'hidden';

      // Focus the close button after animation
      setTimeout(() => {
        const closeBtn = this.drawer.querySelector('[data-cart-drawer-close]');
        if (closeBtn) closeBtn.focus();
      }, 100);

      // Setup focus trap
      this.setupFocusTrap();
    },

    close: function() {
      // Clear any loading states
      this.drawer.querySelectorAll('.is-loading').forEach(el => {
        el.classList.remove('is-loading');
      });

      this.drawer.classList.remove('is-active');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.isOpen = false;
      document.body.style.overflow = '';

      // Restore focus
      if (this.focusedElementBeforeOpen) {
        this.focusedElementBeforeOpen.focus();
      }

      // Remove focus trap listener
      this.removeFocusTrap();
    },

    setupFocusTrap: function() {
      this.focusTrapHandler = (e) => {
        if (e.key !== 'Tab') return;

        const focusableElements = this.drawer.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        const focusableArray = Array.from(focusableElements).filter(el => {
          return el.offsetParent !== null; // Only visible elements
        });

        if (focusableArray.length === 0) return;

        const firstElement = focusableArray[0];
        const lastElement = focusableArray[focusableArray.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      };

      this.drawer.addEventListener('keydown', this.focusTrapHandler);
    },

    removeFocusTrap: function() {
      if (this.focusTrapHandler) {
        this.drawer.removeEventListener('keydown', this.focusTrapHandler);
      }
    },

    updateQuantity: function(item, quantity) {
      const lineIndex = parseInt(item.dataset.lineIndex, 10);

      // Show loading state
      item.classList.add('is-loading');

      // Fallback timeout to remove loading state (3 seconds)
      const fallbackTimeout = setTimeout(() => {
        item.classList.remove('is-loading');
      }, 3000);

      // Update input value immediately for feedback
      const input = item.querySelector(this.selectors.qtyInput);
      if (input) input.value = quantity;

      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          line: lineIndex,
          quantity: quantity
        })
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(cart => {
        this.renderCart(cart);
        this.updateHeaderCartCount(cart.item_count);
      })
      .catch(error => {
        console.error('Error updating cart:', error);
        // Refresh drawer to show correct state
        this.refreshDrawer();
      })
      .finally(() => {
        // Clear fallback and remove loading state
        clearTimeout(fallbackTimeout);
        item.classList.remove('is-loading');
      });
    },

    renderCart: function(cart) {
      // Update count in drawer
      const countEl = this.drawer.querySelector(this.selectors.count);
      if (countEl) countEl.textContent = cart.item_count;

      // Update subtotal
      const subtotalEl = this.drawer.querySelector(this.selectors.subtotal);
      if (subtotalEl) subtotalEl.textContent = this.formatMoney(cart.total_price);

      // Toggle empty/items state
      const emptyEl = this.drawer.querySelector(this.selectors.empty);
      const itemsEl = this.drawer.querySelector(this.selectors.items);
      const footerEl = this.drawer.querySelector(this.selectors.footer);

      if (cart.item_count === 0) {
        emptyEl?.classList.remove('is-hidden');
        itemsEl?.classList.add('is-hidden');
        footerEl?.classList.add('is-hidden');
      } else {
        emptyEl?.classList.add('is-hidden');
        itemsEl?.classList.remove('is-hidden');
        footerEl?.classList.remove('is-hidden');
        // Update items with cart data directly
        this.renderCartItems(cart);
      }

      // Clear any remaining loading states after render
      this.drawer.querySelectorAll('.is-loading').forEach(el => {
        el.classList.remove('is-loading');
      });
    },

    refreshDrawer: function() {
      // Fetch fresh drawer HTML
      fetch(window.location.pathname + '?sections=serge-cart-drawer-section', {
        headers: {
          'Accept': 'application/json'
        }
      })
      .then(response => response.json())
      .then(sections => {
        const sectionHtml = sections['serge-cart-drawer-section'];
        if (sectionHtml) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionHtml, 'text/html');

          // Update items
          const newItems = doc.querySelector(this.selectors.items);
          const currentItems = this.drawer.querySelector(this.selectors.items);
          if (newItems && currentItems) {
            currentItems.innerHTML = newItems.innerHTML;
          }

          // Update count
          const newCount = doc.querySelector(this.selectors.count);
          const currentCount = this.drawer.querySelector(this.selectors.count);
          if (newCount && currentCount) {
            currentCount.textContent = newCount.textContent;
          }

          // Update subtotal
          const newSubtotal = doc.querySelector(this.selectors.subtotal);
          const currentSubtotal = this.drawer.querySelector(this.selectors.subtotal);
          if (newSubtotal && currentSubtotal) {
            currentSubtotal.textContent = newSubtotal.textContent;
          }

          // Update empty/footer visibility
          const newEmpty = doc.querySelector(this.selectors.empty);
          const currentEmpty = this.drawer.querySelector(this.selectors.empty);
          const newFooter = doc.querySelector(this.selectors.footer);
          const currentFooter = this.drawer.querySelector(this.selectors.footer);

          if (newEmpty && currentEmpty) {
            currentEmpty.className = newEmpty.className;
          }
          if (newItems && currentItems) {
            currentItems.className = newItems.className;
          }
          if (newFooter && currentFooter) {
            currentFooter.className = newFooter.className;
          }
        }
      })
      .catch(error => {
        console.error('Error refreshing cart drawer:', error);
        // Fallback: reload drawer by re-fetching cart data
        this.fetchAndRenderCart();
      })
      .finally(() => {
        // Clear any loading states
        this.drawer.querySelectorAll('.is-loading').forEach(el => {
          el.classList.remove('is-loading');
        });
      });
    },

    fetchAndRenderCart: function() {
      fetch('/cart.js', {
        headers: {
          'Accept': 'application/json'
        }
      })
      .then(response => response.json())
      .then(cart => {
        // Update count
        const countEl = this.drawer.querySelector(this.selectors.count);
        if (countEl) countEl.textContent = cart.item_count;

        // Update subtotal
        const subtotalEl = this.drawer.querySelector(this.selectors.subtotal);
        if (subtotalEl) subtotalEl.textContent = this.formatMoney(cart.total_price);

        // Update header count
        this.updateHeaderCartCount(cart.item_count);

        // Toggle visibility based on cart state
        const emptyEl = this.drawer.querySelector(this.selectors.empty);
        const itemsEl = this.drawer.querySelector(this.selectors.items);
        const footerEl = this.drawer.querySelector(this.selectors.footer);

        if (cart.item_count === 0) {
          emptyEl?.classList.remove('is-hidden');
          itemsEl?.classList.add('is-hidden');
          footerEl?.classList.add('is-hidden');
        } else {
          emptyEl?.classList.add('is-hidden');
          itemsEl?.classList.remove('is-hidden');
          footerEl?.classList.remove('is-hidden');
        }

        // Update individual items
        this.renderCartItems(cart);
      })
      .catch(error => {
        console.error('Error fetching cart:', error);
      })
      .finally(() => {
        // Clear any loading states
        this.drawer.querySelectorAll('.is-loading').forEach(el => {
          el.classList.remove('is-loading');
        });
      });
    },

    renderCartItems: function(cart) {
      const itemsContainer = this.drawer.querySelector(this.selectors.items);
      if (!itemsContainer) return;

      if (cart.items.length === 0) {
        itemsContainer.innerHTML = '';
        return;
      }

      let itemsHtml = '';
      cart.items.forEach((item, index) => {
        const lineIndex = index + 1;
        const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';

        itemsHtml += `
          <div class="serge-cart-drawer__item" data-cart-item data-line-index="${lineIndex}" data-variant-id="${item.variant_id}">
            <a href="${item.url}" class="serge-cart-drawer__item-image">
              ${item.image ? `<img src="${this.getSizedImageUrl(item.image, '200x')}" alt="${this.escapeHtml(item.title)}" loading="lazy" width="80" height="80">` : ''}
            </a>
            <div class="serge-cart-drawer__item-details">
              <a href="${item.url}" class="serge-cart-drawer__item-title">${this.escapeHtml(item.product_title)}</a>
              ${variantTitle ? `<p class="serge-cart-drawer__item-variant">${this.escapeHtml(variantTitle)}</p>` : ''}
              <div class="serge-cart-drawer__item-price">
                ${item.original_price !== item.final_price ? `<span style="text-decoration: line-through; opacity: 0.5; margin-right: 0.5rem;">${this.formatMoney(item.original_price)}</span>` : ''}
                ${this.formatMoney(item.final_price)}
              </div>
              <div class="serge-cart-drawer__quantity">
                <button type="button" class="serge-cart-drawer__qty-btn" data-qty-change="-1" aria-label="Decrease quantity">&minus;</button>
                <input type="number" class="serge-cart-drawer__qty-input" value="${item.quantity}" min="0" data-qty-input aria-label="Quantity">
                <button type="button" class="serge-cart-drawer__qty-btn" data-qty-change="1" aria-label="Increase quantity">+</button>
              </div>
            </div>
            <button type="button" class="serge-cart-drawer__remove" data-remove-item aria-label="Remove item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        `;
      });

      itemsContainer.innerHTML = itemsHtml;
    },

    updateHeaderCartCount: function(count) {
      // Update all cart count elements in header
      document.querySelectorAll(this.selectors.headerCartCount).forEach(el => {
        el.textContent = count;
        if (count > 0) {
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });
    },

    formatMoney: function(cents) {
      // Simple money formatting - adjust based on shop currency
      // This assumes PHP currency, adjust as needed
      const amount = (cents / 100).toFixed(2);
      return '₱' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    getSizedImageUrl: function(url, size) {
      if (!url) return '';
      // Shopify image URL size transformation
      return url.replace(/(\.[^.]+)$/, `_${size}$1`);
    },

    escapeHtml: function(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    // Public method to open drawer (can be called from other scripts, e.g., after add to cart)
    openDrawer: function() {
      if (!this.isMobile()) {
        this.open();
      }
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => SergeCartDrawer.init());
  } else {
    SergeCartDrawer.init();
  }

  // Expose to global scope for external access
  window.SergeCartDrawer = SergeCartDrawer;

  // Desktop redirect: If on /cart page and desktop, redirect to homepage and open drawer
  function handleCartPageRedirect() {
    const isCartPage = window.location.pathname === '/cart' || window.location.pathname === '/cart/';
    const isDesktop = window.innerWidth >= MOBILE_BREAKPOINT;

    if (isCartPage && isDesktop) {
      // Redirect to homepage with a flag to open cart drawer
      window.location.href = '/?open_cart=1';
      return;
    }

    // If we arrived at homepage with open_cart flag, open the drawer
    if (window.location.search.includes('open_cart=1')) {
      // Remove the query param from URL without reload
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);

      // Wait for drawer to initialize then open
      setTimeout(() => {
        if (window.SergeCartDrawer && typeof window.SergeCartDrawer.open === 'function') {
          window.SergeCartDrawer.open();
        }
      }, 100);
    }
  }

  // Run redirect check immediately (before full DOM load for faster redirect)
  handleCartPageRedirect();
})();
